/* eslint-disable newline-before-return */
import {
  FieldImpact,
  FormFieldValue,
  RawField,
} from "@/components/FormEngine/types";

import { isPrimitive } from "./typeGuards";

// ========================================
// TYPES POUR PLUS DE CLARTÉ
// ========================================

type FieldsConfiguration = Record<string, RawField>;
type FieldOverrides = Record<string, Partial<RawField>>;
type OverridesBySource = Record<string, FieldOverrides>;

// ========================================
// FUSION DES CONFIGURATIONS
// ========================================

/**
 * Fusionne une configuration de base avec des modifications (overrides)
 * Les modifications écrasent les valeurs de base pour chaque clé
 *
 * @param baseConfiguration - Configuration initiale des champs
 * @param modifications - Modifications à appliquer sur la configuration de base
 * @returns La configuration fusionnée
 */
export const mergeBaseWithOverrides = (
  baseConfiguration: FieldsConfiguration,
  modifications: FieldOverrides,
): FieldsConfiguration => {
  // On commence avec une copie de la configuration de base
  const mergedConfiguration: FieldsConfiguration = { ...baseConfiguration };

  // Pour chaque modification à appliquer
  for (const [fieldKey, fieldModifications] of Object.entries(modifications)) {
    // On récupère le champ existant ou on crée un objet vide
    const existingField = mergedConfiguration[fieldKey] ?? ({} as RawField);

    // On fusionne le champ existant avec les modifications
    mergedConfiguration[fieldKey] = {
      ...existingField,
      ...(fieldModifications as RawField),
    };
  }

  return mergedConfiguration;
};

// ========================================
// AGRÉGATION DES MODIFICATIONS
// ========================================

/**
 * Combine toutes les modifications provenant de différentes sources
 *
 * @param modificationsBySource - Modifications groupées par source
 * @returns Les modifications agrégées pour tous les champs
 */
export const aggregateOverridesBySource = (
  modificationsBySource: OverridesBySource,
): FieldOverrides => {
  const aggregatedModifications: FieldOverrides = {};

  // Pour chaque source de modifications
  for (const modificationsFromOneSource of Object.values(
    modificationsBySource,
  )) {
    // Pour chaque champ modifié par cette source
    for (const [targetFieldKey, fieldModification] of Object.entries(
      modificationsFromOneSource,
    )) {
      // On récupère les modifications déjà agrégées pour ce champ
      const existingModifications =
        aggregatedModifications[targetFieldKey] ?? {};

      // On gère spécialement la propriété "hidden" avec une logique OR
      const wasFieldHidden = getHiddenValue(existingModifications);
      const isFieldNowHidden = getHiddenValue(fieldModification);
      const shouldFieldBeHidden = wasFieldHidden || isFieldNowHidden;

      // On fusionne les modifications
      aggregatedModifications[targetFieldKey] = {
        ...existingModifications,
        ...fieldModification,
        // Si le champ doit être caché, on surcharge la propriété hidden
        ...(shouldFieldBeHidden ? { hidden: true } : {}),
      };
    }
  }

  return aggregatedModifications;
};

/**
 * Extrait la valeur booléenne de la propriété "hidden" d'un objet
 *
 * @param fieldData - L'objet contenant potentiellement une propriété hidden
 * @returns true si hidden est true, false sinon
 */
function getHiddenValue(fieldData: any): boolean {
  return typeof fieldData.hidden === "boolean" ? fieldData.hidden : false;
}

// ========================================
// EXTRACTION DE VALEURS
// ========================================

/**
 * Extrait la valeur primitive à sauvegarder depuis un changement de formulaire
 * Retourne soit la valeur directe si c'est une primitive, soit l'ID de l'option sélectionnée
 *
 * @param changedValue - La nouvelle valeur du champ de formulaire
 * @returns La valeur primitive à stocker
 */
export const primitiveFromChange = (
  changedValue: FormFieldValue,
): string | number | boolean | null | undefined => {
  // Si la valeur est déjà une primitive, on la retourne directement
  if (isPrimitive(changedValue?.value)) {
    return changedValue.value;
  }

  // Sinon, on retourne l'ID (cas d'une sélection dans une liste)
  return changedValue?.id ?? undefined;
};

// ========================================
// CONSTRUCTION DES MODIFICATIONS
// ========================================

/**
 * Construit l'ensemble des modifications à appliquer suite à un changement de valeur
 * Inclut les modifications du champ source et les impacts sur d'autres champs
 *
 * @param sourceFieldKey - Clé du champ qui a changé
 * @param newValue - Nouvelle valeur du champ
 * @param fieldImpacts - Liste des impacts sur d'autres champs
 * @returns Les modifications à appliquer et la liste des champs impactés
 */
export const buildSourceOverridesSlot = (
  sourceFieldKey: string,
  newValue: FormFieldValue,
  fieldImpacts?: FieldImpact[],
): {
  slot: FieldOverrides;
  impactedTargets: string[];
} => {
  // On commence par stocker la nouvelle valeur du champ source
  const modifications: FieldOverrides = {
    [sourceFieldKey]: {
      value: primitiveFromChange(newValue),
    },
  };

  // Liste des champs qui seront impactés
  const impactedFieldKeys: string[] = [];

  // Si des impacts sont définis
  if (Array.isArray(fieldImpacts)) {
    for (const impact of fieldImpacts) {
      // On construit le chemin du champ impacté
      const targetFieldPath = buildFieldPath(impact.path);

      // Si le chemin est invalide, on passe au suivant
      if (!targetFieldPath) continue;

      // On ajoute le champ à la liste des impactés
      impactedFieldKeys.push(targetFieldPath);

      // On prépare les modifications pour ce champ impacté
      const fieldModifications = prepareFieldModifications(impact);

      // On fusionne avec les modifications existantes pour ce champ
      modifications[targetFieldPath] = {
        ...(modifications[targetFieldPath] ?? {}),
        ...fieldModifications,
      };
    }
  }

  return {
    slot: modifications,
    impactedTargets: impactedFieldKeys,
  };
};

/**
 * Construit un chemin de champ à partir d'un tableau de segments
 *
 * @param pathSegments - Tableau des segments du chemin
 * @returns Le chemin complet sous forme de string, ou vide si invalide
 */
function buildFieldPath(pathSegments: any): string {
  if (!Array.isArray(pathSegments) || pathSegments.length === 0) {
    return "";
  }
  return pathSegments.join(".");
}

/**
 * Prépare les modifications pour un champ impacté
 * Si le champ est caché, sa valeur est réinitialisée
 *
 * @param impact - L'impact à appliquer
 * @returns Les modifications préparées
 */
function prepareFieldModifications(impact: FieldImpact): Partial<RawField> {
  const modifications = { ...(impact.properties as Partial<RawField>) };

  // Si on cache le champ, on réinitialise sa valeur
  if ((modifications as any).hidden === true) {
    (modifications as any).value = undefined;
  }

  return modifications;
}

// ========================================
// APPLICATION DES TRANSITIONS
// ========================================

/**
 * Applique une transition de modifications en :
 * 1. Nettoyant les modifications obsolètes des autres sources
 * 2. Plaçant la source actuelle en dernier pour la priorité
 *
 * @param currentModificationsBySource - État actuel des modifications par source
 * @param activeSourceKey - Clé de la source qui vient de changer
 * @param newModifications - Nouvelles modifications à appliquer
 * @param fieldsToClean - Liste des champs à nettoyer dans les autres sources
 * @returns Le nouvel état des modifications par source
 */
export const applyOverridesTransition = (
  currentModificationsBySource: OverridesBySource,
  activeSourceKey: string,
  newModifications: FieldOverrides,
  fieldsToClean: string[],
): OverridesBySource => {
  // Étape 1: Cloner l'état actuel
  const updatedModificationsBySource = cloneModificationsBySource(
    currentModificationsBySource,
  );

  // Étape 2: Nettoyer les champs impactés dans les autres sources
  if (fieldsToClean.length > 0) {
    cleanImpactedFieldsInOtherSources(
      updatedModificationsBySource,
      activeSourceKey,
      fieldsToClean,
    );
  }

  // Étape 3: Réorganiser pour mettre la source active en dernier (priorité maximale)
  return reorderWithSourceLast(
    updatedModificationsBySource,
    activeSourceKey,
    newModifications,
  );
};

/**
 * Clone superficiellement les modifications par source
 *
 * @param modificationsBySource - Les modifications à cloner
 * @returns Une copie superficielle
 */
function cloneModificationsBySource(
  modificationsBySource: OverridesBySource,
): OverridesBySource {
  const cloned: OverridesBySource = {};

  for (const [sourceKey, sourceModifications] of Object.entries(
    modificationsBySource,
  )) {
    cloned[sourceKey] = { ...sourceModifications };
  }

  return cloned;
}

/**
 * Nettoie les champs impactés dans toutes les sources sauf la source active
 *
 * @param modificationsBySource - Les modifications par source (modifié en place)
 * @param activeSourceKey - La source à ne pas toucher
 * @param fieldsToClean - Les champs à nettoyer
 */
function cleanImpactedFieldsInOtherSources(
  modificationsBySource: OverridesBySource,
  activeSourceKey: string,
  fieldsToClean: string[],
): void {
  const impactedFieldsSet = new Set(fieldsToClean);

  for (const [sourceKey, sourceModifications] of Object.entries(
    modificationsBySource,
  )) {
    // On ne touche pas à la source active
    if (sourceKey === activeSourceKey) continue;

    let hasRemovedFields = false;

    // On supprime les champs impactés
    for (const targetField of Object.keys(sourceModifications)) {
      if (impactedFieldsSet.has(targetField)) {
        delete sourceModifications[targetField];
        hasRemovedFields = true;
      }
    }

    // Si la source n'a plus de modifications, on la supprime complètement
    if (hasRemovedFields && Object.keys(sourceModifications).length === 0) {
      delete modificationsBySource[sourceKey];
    }
  }
}

/**
 * Réorganise les modifications en plaçant une source spécifique en dernier
 * Cela lui donne la priorité maximale lors de l'agrégation
 *
 * @param modificationsBySource - Les modifications actuelles
 * @param sourceKeyToMoveLast - La source à placer en dernier
 * @param newModificationsForSource - Les nouvelles modifications pour cette source
 * @returns Les modifications réorganisées
 */
function reorderWithSourceLast(
  modificationsBySource: OverridesBySource,
  sourceKeyToMoveLast: string,
  newModificationsForSource: FieldOverrides,
): OverridesBySource {
  // On retire la source de l'objet actuel
  const { [sourceKeyToMoveLast]: _removed, ...otherSources } =
    modificationsBySource;

  // On la remet à la fin avec ses nouvelles modifications
  return {
    ...otherSources,
    [sourceKeyToMoveLast]: newModificationsForSource,
  };
}
